import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from 'contexts/AuthContext';
import { Button } from 'components';
import styles from "./RegisterForm.module.scss"
export default function RegisterForm({ onSuccess }) {
  const { register, verifyRegistration, resendVerificationCode } = useAuth();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Состояние для модального окна верификации
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [timer, setTimer] = useState('');
  const [verificationError, setVerificationError] = useState(null);
  
  // Отслеживание изменения состояния модального окна
  React.useEffect(() => {
    console.log('🔄 Modal state changed:', showVerificationModal);
    if (showVerificationModal) {
      console.log('   📧 Email:', registeredEmail);
      console.log('   ⏰ Expires:', expiresAt);
    }
  }, [showVerificationModal, registeredEmail, expiresAt]);

  // Таймер обратного отсчета
  React.useEffect(() => {
    if (!expiresAt) return;
    
    const updateTimer = () => {
      const now = new Date();
      const expires = new Date(expiresAt);
      const diff = expires - now;
      
      if (diff <= 0) {
        setTimer('Код истёк');
        return;
      }
      
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimer(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== passwordConfirm) {
      setError('Пароли не совпадают');
      return;
    }
    setLoading(true);
    const fd = new FormData();
    fd.append('username', email);
    fd.append('email', email);
    fd.append('password', password);
    fd.append('password_confirm', passwordConfirm);
    fd.append('first_name', '');
    fd.append('last_name', '');
    if (phone) fd.append('phone', phone);
    if (photo) fd.append('avatar', photo);
    
    console.log('📤 Submitting registration...');
    const res = await register(fd);
    setLoading(false);
    
    console.log('📥 Registration response:', res);
    console.log('   - res.ok:', res.ok);
    console.log('   - res.data:', res.data);
    console.log('   - res.error:', res.error);
    
    // Проверяем успешность
    if (res.ok && res.data) {
      console.log('✅ Step 1 complete: User created, code sent');
      console.log('   - Full response data:', JSON.stringify(res.data, null, 2));
      
      // Открываем модальное окно для ввода кода
      const emailToUse = res.data.email || email; // Используем email из ответа или из формы
      
      setRegisteredEmail(emailToUse);
      setExpiresAt(res.data.expires_at);
      
      console.log('🔓 Opening verification modal...');
      console.log('   - Email:', emailToUse);
      console.log('   - Expires at:', res.data.expires_at);
      
      // Небольшая задержка для плавного перехода
      setTimeout(() => {
        setShowVerificationModal(true);
        console.log('✅ Modal should be visible now!');
      }, 100);
    } else {
      console.error('❌ Registration failed or incomplete response');
      console.error('   res:', res);
      setError(res.error || 'Ошибка регистрации. Попробуйте снова.');
    }
  };
  
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setVerificationError(null);
    setVerifying(true);
    
    console.log('🔐 Verifying code:', verificationCode);
    const res = await verifyRegistration(registeredEmail, verificationCode);
    setVerifying(false);
    
    console.log('📥 Verification response:', res);
    
    if (res.ok && res.data) {
      console.log('✅ Registration completed successfully!');
      console.log('   - User:', res.data.user);
      console.log('   - Access token:', res.data.access ? 'received' : 'missing');
      
      // Токены уже сохранены в AuthContext, но сохраним дополнительно для совместимости
      if (res.data.access) localStorage.setItem('token', res.data.access);
      
      // Закрываем модалку
      setShowVerificationModal(false);
      
      console.log('🔄 Reloading page to update auth state...');
      
      // Перезагружаем страницу для полного обновления состояния
      setTimeout(() => {
        window.location.reload();
      }, 300);
    } else {
      console.error('❌ Verification failed:', res.error);
      setVerificationError(res.error || 'Неверный код');
    }
  };
  
  const handleResendCode = async () => {
    setVerificationError(null);
    setResending(true);
    
    const res = await resendVerificationCode(registeredEmail);
    setResending(false);
    
    if (res.ok && res.data) {
      setExpiresAt(res.data.expires_at);
      setVerificationError(null);
      alert('Новый код отправлен на ваш email');
    } else {
      setVerificationError(res.error || 'Ошибка отправки кода');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 8 }}>
        <label className="auth-label">Email</label>
        <input className="auth-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="you@example.com" />
      </div>
      <div style={{ marginBottom: 8 }}>
        <label className="auth-label">Телефон</label>
        <input className="auth-input" value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="+996 700 000000" />
      </div>
      <div style={{ marginBottom: 8 }}>
        <label className="auth-label">Пароль</label>
        <div className="auth-field">
          <input className="auth-input" value={password} onChange={(e) => setPassword(e.target.value)} type={showPassword ? 'text' : 'password'} required placeholder="********" />
          <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>{showPassword ? '🙈' : '👁️'}</button>
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        <label className="auth-label">Подтвердите пароль</label>
        <div className="auth-field">
          <input className="auth-input" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} type={showPassword ? 'text' : 'password'} required placeholder="********" />
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        <label className="auth-label">Аватар (опционально)</label>
        <div className="avatar-upload">
          {photoPreview && (
            <div className={styles.avatar}>
              <img src={photoPreview} alt="avatar preview" />
            </div>
          )}
          <label className="avatar-file-label">
            Загрузить фото
            <input
              type="file"
              accept="image/*"
              className="avatar-file-input"
              onChange={e => {
                const file = e.target.files[0];
                setPhoto(file);
                if (file) {
                  const reader = new FileReader();
                  reader.onload = ev => setPhotoPreview(ev.target.result);
                  reader.readAsDataURL(file);
                } else {
                  setPhotoPreview(null);
                }
              }}
            />
          </label>
        </div>
      </div>
      {error && (
        <div className="auth-error">
          {typeof error === 'string' ? error : ''}
          {error && typeof error === 'object' && (
            error.detail ? String(error.detail) :
            error.message ? String(error.message) :
            error.email ? `Email: ${error.email}` :
            error.username ? `Username: ${error.username}` :
            error.password ? `Password: ${error.password}` :
            Object.values(error).flat ? String(Object.values(error).flat()[0]) :
            JSON.stringify(error)
          )}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button className="auth__btn" type="submit" style={{ width: '100%' }} disabled={loading}>{loading ? 'Регистрируем...' : 'Зарегистрироваться'}</Button>
      </div>
      
      {/* Модальное окно верификации через Portal */}
      {showVerificationModal && (() => {
        console.log('🎨 RENDERING MODAL - showVerificationModal is TRUE');
        const modalContent = (
          <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowVerificationModal(false)}>
            <div className="modal-content" style={{ maxWidth: '450px', padding: '35px', borderRadius: '16px' }}>
              <h3 style={{ marginBottom: '10px', fontSize: '24px', color: '#1a1a1a', textAlign: 'center' }}>📧 Подтвердите email</h3>
            <p style={{ marginBottom: '25px', color: '#666', textAlign: 'center', lineHeight: '1.5' }}>
              Мы отправили 6-значный код на<br/>
              <strong style={{ color: '#333', fontSize: '16px' }}>{registeredEmail}</strong>
            </p>
            
            <form onSubmit={handleVerifyCode}>
              <div style={{ marginBottom: '20px' }}>
                <label className="auth-label" style={{ marginBottom: '10px', display: 'block', fontSize: '14px', fontWeight: '500' }}>
                  Введите код из письма
                </label>
                <input 
                  className="auth-input" 
                  value={verificationCode} 
                  onChange={(e) => setVerificationCode(e.target.value)} 
                  type="text" 
                  required 
                  placeholder="• • • • • •"
                  maxLength="6"
                  autoFocus
                  style={{ 
                    fontSize: '24px', 
                    textAlign: 'center', 
                    letterSpacing: '8px',
                    fontWeight: '600',
                    padding: '15px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    color: '#333'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
                {timer && (
                  <div style={{ 
                    marginTop: '10px', 
                    fontSize: '13px', 
                    textAlign: 'center',
                    padding: '8px',
                    borderRadius: '6px',
                    background: timer === 'Код истёк' ? '#ffebee' : '#f5f5f5',
                    color: timer === 'Код истёк' ? '#d32f2f' : '#666',
                    fontWeight: '500'
                  }}>
                    {timer === 'Код истёк' ? '⏰ Код истёк. Запросите новый код' : `⏱️ Код действителен: ${timer}`}
                  </div>
                )}
              </div>
              
              {verificationError && (
                <div className="auth-error" style={{ marginBottom: '20px', padding: '12px', borderRadius: '8px' }}>
                  {typeof verificationError === 'string' ? verificationError : ''}
                  {verificationError && typeof verificationError === 'object' && (
                    verificationError.detail ? String(verificationError.detail) :
                    verificationError.error ? String(verificationError.error) :
                    JSON.stringify(verificationError)
                  )}
                </div>
              )}
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '15px' }}>
                <Button 
                  type="submit" 
                  style={{ 
                    width: '100%',
                    padding: '14px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: verifying ? 'not-allowed' : 'pointer',
                    opacity: verifying ? 0.7 : 1,
                    transition: 'all 0.2s'
                  }} 
                  disabled={verifying}
                >
                  {verifying ? '⏳ Проверяем...' : '✓ Подтвердить код'}
                </Button>
                <Button 
                  type="button" 
                  onClick={handleResendCode} 
                  disabled={resending}
                  style={{ 
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    background: '#f5f5f5',
                    color: '#666',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    cursor: resending ? 'not-allowed' : 'pointer',
                    opacity: resending ? 0.7 : 1,
                    transition: 'all 0.2s'
                  }}
                >
                  {resending ? '📧 Отправляем...' : '🔄 Отправить код повторно'}
                </Button>
              </div>
              
              <button 
                type="button" 
                onClick={() => setShowVerificationModal(false)}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  border: 'none', 
                  background: 'transparent', 
                  color: '#999', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.color = '#666'}
                onMouseLeave={(e) => e.target.style.color = '#999'}
              >
                ← Назад к регистрации
              </button>
            </form>
          </div>
        </div>
        );
        
        // Рендерим модальное окно через Portal в body
        return ReactDOM.createPortal(modalContent, document.body);
      })()}
    </form>
  );
}
